<template>
  <ul v-if="loading" class="gs-results-list gs-skeleton" role="status" aria-live="polite">
    <li v-for="i in 3" :key="`skeleton-${i}`">
      <div class="gs-result-item gs-navigation-section">
        <div class="gs-navigation-icon" aria-hidden="true">
          <zm-skeleton-item variant="circle" style="width: 40px; height: 40px" />
        </div>
        <div class="gs-navigation-content">
          <p class="gs-navigation-name">
            <zm-skeleton-item variant="text" :percent="80" />
          </p>
          <p class="gs-navigation-path">
            <zm-skeleton-item variant="text" :percent="60" />
          </p>
        </div>
      </div>
    </li>
  </ul>
  <ul
    v-else-if="routeList.length > 0"
    class="gs-results-list"
    :aria-label="t('aria.navigationResults', [routeList.length])">
    <li v-for="route in routeList" :key="route.id">
      <a
        class="gs-result-item gs-navigation-section"
        href="javascript: void(0)"
        @click="onNavigation(route)">
        <div v-if="route.icon?.render" class="gs-navigation-icon" aria-hidden="true">
          <zm-icon :size="20">
            <component :is="route.icon.render" />
          </zm-icon>
        </div>
        <div class="gs-navigation-content">
          <p class="gs-navigation-name">
            <zm-highlight-text
              :key="`${route.label}::${activeQuery}`"
              :content="route.label || ''"
              :highlight="highlightKeywords" />
          </p>
          <p class="gs-navigation-path">
            <zm-highlight-text
              :key="`${route.id}-path::${activeQuery}`"
              :content="route.breadcrumbs.join(' > ')"
              :highlight="highlightKeywords" />
          </p>
        </div>
      </a>
    </li>
  </ul>
  <div v-else class="gs-empty" role="status">
    <img src="/assets/search-empty.png" width="200" height="200" alt="" />
    <p class="empty-main">{{ $t('searchEmpty') }}</p>
    <p class="empty-sub">{{ $t('noNavigation') }}</p>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { ZmHighlightText, ZmIcon, ZmSkeletonItem } from '@zoom/zoom-ui-vue3'
import { transferMenu2Document } from './menus-to-documents'
import { searchService, convertMenusToSearchDocs } from './navigation-search'
import { createMenuIconMap } from './menu-utils'
import { toHighlightKeywords } from './highlight.js'
import { trackEvent } from '@/shared/event-tracker'
const { t } = useI18n()

const routeList = ref([])
const loading = ref(false)
const initialized = ref(false)
const menuIconMap = ref(new Map())
const activeQuery = ref('')
const highlightKeywords = computed(() => toHighlightKeywords(activeQuery.value))
let abortController = null
const retryTimer1 = ref(null)
const retryTimer2 = ref(null)

const cancel = () => {
  abortController?.abort()
  abortController = null
  loading.value = false
}

const initSearchService = async () => {
  if (!window.sideMenu?.menusData) {
    console.warn('⚠️ window.sideMenu.menusData is not available yet')
    return false
  }

  if (!Array.isArray(window.sideMenu.menusData) || window.sideMenu.menusData.length === 0) {
    console.warn('⚠️ window.sideMenu.menusData is empty')
    return false
  }

  if (initialized.value && searchService.getDocumentCount() > 0) {
    return true
  }

  try {
    const menuItems = transferMenu2Document(window.sideMenu.menusData)

    if (menuItems.length === 0) {
      console.warn('⚠️ No valid menu items after conversion')
      return false
    }

    const searchDocs = convertMenusToSearchDocs(menuItems)

    if (searchDocs.length === 0) {
      console.warn('⚠️ No valid search documents after conversion')
      return false
    }

    menuIconMap.value = createMenuIconMap()

    await searchService.init(searchDocs)
    initialized.value = true

    return true
  } catch (error) {
    console.error('❌ Failed to initialize navigation search:', error)
    initialized.value = false
    return false
  }
}

const search = async (keyword) => {
  abortController?.abort()
  abortController = null

  const nextQuery = keyword?.trim() ?? ''
  if (!nextQuery) {
    activeQuery.value = ''
    routeList.value = []
    loading.value = false
    return 0
  }

  const controller = new AbortController()
  abortController = controller
  const { signal } = controller
  activeQuery.value = nextQuery

  if (!initialized.value || searchService.getDocumentCount() === 0) {
    let retries = 3
    let success = false

    while (retries > 0 && !success && !signal.aborted) {
      success = await initSearchService()
      if (!success) {
        retries--
        if (retries > 0 && !signal.aborted) {
          await new Promise((resolve, reject) => {
            if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'))
            const timer = setTimeout(resolve, 500)
            signal.addEventListener(
              'abort',
              () => {
                clearTimeout(timer)
                reject(new DOMException('Aborted', 'AbortError'))
              },
              { once: true }
            )
          }).catch(() => {
            retries = 0
          })
        }
      }
    }

    if (signal.aborted) return 0

    if (!success) {
      console.error('❌ Failed to initialize search service after retries')
      return 0
    }
  }

  loading.value = true
  try {
    const result = await searchService.search(nextQuery, {
      limit: 20,
      threshold: 0.2
    })
    if (signal.aborted) return 0

    routeList.value = result.hits
      .map((hit) => hit.document)
      .map((menu) => ({
        ...menu,
        icon: menuIconMap.value.get(menu.id)
      }))
    return routeList.value.length
  } catch (error) {
    if (signal.aborted) return 0
    console.error('❌ Search navigation failed:', error)
    routeList.value = []
    return 0
  } finally {
    if (abortController === controller) loading.value = false
  }
}

const handleNavigation = (url) => {
  if (url) {
    window.location.href = url
  }
}

const onNavigation = (route) => {
  trackEvent({
    linkText: `${route.breadcrumbs.join(' > ')}`,
    linkUrl: route.url,
    uiLocation: 'web portal global search - navigation'
  })
  setTimeout(() => {
    window.location.href = route.url
  }, 100)
}

onMounted(async () => {
  let success = await initSearchService()

  if (!success) {
    retryTimer1.value = setTimeout(async () => {
      success = await initSearchService()

      if (!success) {
        retryTimer2.value = setTimeout(async () => {
          await initSearchService()
        }, 2000)
      }
    }, 1000)
  }
})

onBeforeUnmount(() => {
  if (retryTimer1.value) clearTimeout(retryTimer1.value)
  if (retryTimer2.value) clearTimeout(retryTimer2.value)
})

defineExpose({
  search,
  cancel
})
</script>

<style lang="scss">
.gs-navigation-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 8px;
  text-align: left;

  .gs-navigation-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 6px;
    color: var(--zoom-color-text-strong-neutral, #555b62);
    border-radius: 10px;
    background: var(--zoom-color-state-subtle-disable, #f1f4f6);
  }

  .gs-navigation-content {
    flex: 1;
    min-width: 0;
  }
}

.gs-skeleton {
  pointer-events: none;

  .gs-navigation-section {
    cursor: default;
  }
}
</style>
